import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Heartbeat, ChatText, Pause, Play, Stop, Sparkle, User, GraduationCap } from '@phosphor-icons/react';
import api from '../api';
import { toast } from 'sonner';

/**
 * GuidedDemoPanel
 *
 * Picture-in-picture panel that runs the shared "Guided Demo" onboarding
 * experience. It creates a real `simulation_session` with
 * `is_guided_demo=true`, then scripts real backend moves (FC change, rhythm,
 * message, pause/resume/end) that would come from an instructor. The student
 * side (top) and the instructor side (bottom) are two mini-views of the
 * SAME session — the session is the source of truth, we just poll it.
 *
 * All backend calls guarantee this session is excluded from stats, history,
 * quotas and evaluation.
 */
export default function GuidedDemoPanel({ open, onClose }) {
  const [session, setSession] = useState(null); // { session_id, ... }
  const [status, setStatus] = useState(null);   // { vitals, status, timeline }
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const resp = await api.post('/tour/demo/start', { role: 'alumno' });
        setSession(resp.data);
        await refresh(resp.data.session_id);
      } catch (e) {
        toast.error('No se pudo iniciar la demo guiada');
      }
    })();
    // eslint-disable-next-line
  }, [open]);

  // Poll status while open — WebSocket may or may not be wired for this route
  useEffect(() => {
    if (!open || !session?.session_id) return;
    const id = setInterval(() => refresh(session.session_id), 1500);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [open, session?.session_id]);

  const refresh = async (sid) => {
    try {
      const r = await api.get(`/tour/demo/status/${sid}`);
      setStatus(r.data);
    } catch {}
  };

  const doMove = async (action, value) => {
    if (!session?.session_id || busy) return;
    setBusy(true);
    try {
      await api.post('/tour/demo/instructor-move', {
        session_id: session.session_id, action, value,
      });
      await refresh(session.session_id);
      setStep(s => s + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const scriptedSteps = [
    { key: 'fc_change', label: 'Instructor baja FC 168 → 72', fn: () => doMove('fc_change', '72') },
    { key: 'rhythm_change', label: 'Ritmo TSV → sinusal', fn: () => doMove('rhythm_change', 'sinusal') },
    { key: 'message', label: 'Enviar mensaje al alumno', fn: () => doMove('message', 'Excelente evolución tras adenosina.') },
    { key: 'pause', label: 'Pausar simulación', fn: () => doMove('pause') },
    { key: 'resume', label: 'Reanudar simulación', fn: () => doMove('resume') },
    { key: 'end', label: 'Finalizar simulación', fn: () => doMove('end') },
  ];

  const cleanup = async () => {
    if (session?.session_id) {
      try { await api.delete(`/tour/demo/${session.session_id}`); } catch {}
    }
    setSession(null);
    setStatus(null);
    setStep(0);
    onClose?.();
  };

  if (!open) return null;

  const vitals = status?.vitals || { fc: 168, ritmo_ecg: 'TSV', pa_sistolica: 105, pa_diastolica: 60, satO2: 96 };
  const isPaused = status?.status === 'pausada';
  const isEnded = status?.status === 'finalizada';
  const lastMsg = (status?.timeline || []).slice().reverse().find(t => t.tipo === 'mensaje_instructor');

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[9999] w-[420px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
         data-testid="guided-demo-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2.5 flex items-center gap-2">
        <Sparkle size={16} weight="fill" />
        <span className="text-sm font-semibold flex-1">Demo Guiada · Supervisión en vivo</span>
        <button onClick={cleanup} className="text-white/80 hover:text-white text-xs">Cerrar ✕</button>
      </div>

      {/* Explainer */}
      <div className="px-4 py-2 bg-cyan-50 border-b border-cyan-100 text-[11px] text-cyan-900 leading-tight">
        Alumno e Instructor están conectados a la <b>misma sesión</b>. Los cambios del instructor aparecen en la vista del alumno en tiempo real.
        Esta sesión <b>no consume cupos</b> ni aparece en tu historial.
      </div>

      {/* Alumno view */}
      <div className="p-3 border-b border-slate-100">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1.5">
          <GraduationCap size={12} /> Vista del Alumno
        </div>
        <div className="grid grid-cols-4 gap-2 text-center">
          <VitalTile label="FC" value={vitals.fc} unit="lpm" color="#dc2626" />
          <VitalTile label="Ritmo" value={vitals.ritmo_ecg} color="#0891b2" small />
          <VitalTile label="PA" value={`${vitals.pa_sistolica}/${vitals.pa_diastolica}`} color="#7c3aed" small />
          <VitalTile label="SpO₂" value={`${vitals.satO2}%`} color="#059669" />
        </div>
        {isPaused && (
          <div className="mt-2 rounded bg-amber-50 border border-amber-200 text-amber-900 text-[11px] px-2 py-1">
            ⏸ Simulación pausada por instructor
          </div>
        )}
        {isEnded && (
          <div className="mt-2 rounded bg-slate-50 border border-slate-200 text-slate-700 text-[11px] px-2 py-1">
            ✓ Simulación finalizada — genera evaluación y debriefing
          </div>
        )}
        {lastMsg && (
          <div className="mt-2 rounded bg-blue-50 border border-blue-200 text-blue-900 text-[11px] px-2 py-1 flex items-start gap-1.5">
            <ChatText size={12} className="mt-0.5" />
            <span><b>Instructor:</b> {lastMsg.descripcion.replace(/^Instructor: /,'')}</span>
          </div>
        )}
      </div>

      {/* Instructor scripted actions */}
      <div className="p-3">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1.5">
          <User size={12} /> Vista del Instructor (guiado)
        </div>
        <div className="space-y-1.5">
          {scriptedSteps.map((s, i) => (
            <button
              key={s.key}
              onClick={s.fn}
              disabled={busy || i > step}
              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs border transition ${
                i < step ? 'bg-green-50 border-green-200 text-green-700' :
                i === step ? 'bg-white border-cyan-400 text-slate-900 hover:bg-cyan-50 shadow-sm' :
                'bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed'
              }`}
              data-testid={`demo-step-${s.key}`}
            >
              <span className="inline-block w-4 text-center text-[10px] mr-1.5">
                {i < step ? '✓' : (i === step ? '▶' : i + 1)}
              </span>
              {s.label}
            </button>
          ))}
        </div>
        {step >= scriptedSteps.length && (
          <Button size="sm" onClick={cleanup} className="w-full mt-2" data-testid="demo-finish-btn">
            Cerrar demo guiada
          </Button>
        )}
      </div>
    </div>,
    document.body
  );
}

function VitalTile({ label, value, unit, color, small }) {
  return (
    <div className="rounded-lg bg-slate-900 text-white px-2 py-1.5">
      <div className="text-[9px] opacity-60">{label}</div>
      <div className={`font-bold leading-none ${small ? 'text-sm mt-1' : 'text-lg'}`} style={{ color }}>
        {value}
        {unit && <span className="text-[9px] opacity-60 ml-0.5">{unit}</span>}
      </div>
    </div>
  );
}
